<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import type { NumberFieldRootProps } from 'reka-ui';
import {
  NumberFieldDecrement,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldRoot,
  useForwardPropsEmits,
} from 'reka-ui';
import { computed, onMounted, ref, useSlots } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import { cnMerge } from '@/utils/utils.js';
import IconAdd from '~icons/ion/add-outline';
import IconRemove from '~icons/ion/remove-outline';

import KaDescription from '../KaDescription/KaDescription.vue';
import KaLabel from '../KaLabel/KaLabel.vue';
import KaMessage from '../KaMessage/KaMessage.vue';
import type { KaVeeFormFieldEmitContract } from '../KaVeeFormField';
import type { KaInputNumberBaseProps } from './types';

const props = withDefaults(defineProps<NumberFieldRootProps & KaInputNumberBaseProps>(), {
  // KaInputNumberProps default props
  hideLabel: false,
  hideDescription: false,
  hideErrorMessage: false,
  mode: 'passive-aggressive',
  showErrorStylesOnly: false,
  width: 'full',
  modelValue: undefined,
  // Reka-UI NumberFieldRoot default props
  disableWheelChange: true,
  formatOptions: () => {
    return { useGrouping: false };
  },
});
const emits = defineEmits<
  KaVeeFormFieldEmitContract<Event> & {
    (e: 'update:modelValue', payload: number): void;
  }
>();

defineSlots<{
  description(props: { description: KaInputNumberBaseProps['description'] }): void;
  label(props: { label: KaInputNumberBaseProps['label'] }): void;
}>();

const slots = useSlots();

defineOptions({ inheritAttrs: false });

const delegatedProps = reactiveOmit(props, [
  'class',
  'containerClasses',
  'dataTestid',
  'description',
  'errorMessage',
  'errorMessageId',
  'hideDescription',
  'hideErrorMessage',
  'hideLabel',
  'placeholder',
  'inputContainerClasses',
  'label',
  'showErrorStylesOnly',
  'width',
] satisfies (keyof KaInputNumberBaseProps)[]);

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const labelId = computed(() => `${props.id}-label`);
const id = computed(() => props.id ?? props.name);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const descriptionId = computed(() =>
  props.description || slots.description ? `${id.value}-description` : undefined,
);
const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const hasError = computed(() => props.errorMessage !== undefined);
const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});

const isRequired = computed(() => props.required || props.ariaRequired);

const numberFieldRootRef = ref<InstanceType<typeof NumberFieldRoot> | null>(null);
onMounted(() => {
  const hiddenInput = numberFieldRootRef.value?.$el?.querySelector('input[data-hidden]');
  if (hiddenInput) {
    hiddenInput.setAttribute('aria-hidden', 'true');
    hiddenInput.setAttribute('tabindex', '-1');
  }
});
</script>

<template>
  <div :class="cnMerge('w-full', props.containerClasses)">
    <NumberFieldRoot ref="numberFieldRootRef" v-bind="forwarded" :id="id">
      <KaLabel
        :id="labelId"
        :data-testid="suffix('label')"
        :for="id"
        :hide-label="props.hideLabel"
        :class="!shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
        :required="isRequired"
        :hide-required-asterisk="props.hideRequiredAsterisk"
      >
        <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
      </KaLabel>

      <KaDescription
        v-if="props.description || $slots.description"
        :id="descriptionId"
        :data-testid="suffix('description')"
        :description="props.description"
        :hide-description="props.hideDescription"
        :class="shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
      >
        <slot name="description" v-bind="{ description: props.description }" />
      </KaDescription>

      <div
        :class="
          cnMerge(
            'relative',
            props.width === 'full' ? 'w-full' : 'w-fit',
            props.inputContainerClasses,
          )
        "
      >
        <NumberFieldInput
          v-bind="$attrs"
          :placeholder="props.placeholder"
          :data-testid="dataTestid"
          :aria-describedby="descriptionId"
          :aria-invalid="props.showErrorStylesOnly || hasError"
          :aria-errormessage="computedErrorMessageId"
          :aria-required="isRequired"
          :class="
            cnMerge(
              'form-input block w-full rounded border-0 py-1.5 text-sm leading-6 shadow-sm ring-1 ring-inset [-moz-appearance:_textfield] focus:ring-2 focus:ring-inset dark:bg-navy-800 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none',
              props.showErrorStylesOnly || hasError
                ? 'text-red-600 ring-red-500/60 placeholder:text-red-500 focus:ring-red-500/80 dark:text-red-400'
                : 'text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-blue-400 dark:text-gray-50 dark:ring-navy-600 dark:focus:ring-blue-500',
              props.class,
            )
          "
          @blur="emits('blur', $event)"
        />

        <div class="absolute inset-y-0 right-0 flex items-center pr-2">
          <NumberFieldDecrement
            :data-testid="suffix('decrement-button')"
            class="p-0.5 hover:bg-gray-50 focus-visible:outline-blue-400 dark:hover:bg-white/5"
          >
            <IconRemove class="size-4" />
          </NumberFieldDecrement>
          <NumberFieldIncrement
            :data-testid="suffix('increment-button')"
            class="p-0.5 hover:bg-gray-50 focus-visible:outline-blue-400 dark:hover:bg-white/5"
          >
            <IconAdd class="size-4" />
          </NumberFieldIncrement>
        </div>
      </div>
    </NumberFieldRoot>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
    />
  </div>
</template>
